import { Download, List, Map as MapIcon, Plus, SearchX, Store, Trash2 } from 'lucide-react';
import { type FormEvent, useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageHead } from '@/admin-area/ui/PageHead';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { useAuth } from '@/auth/AuthProvider';
import { ClickableRow } from '@/components/list/ClickableRow';
import { optionLabel, type FilterField, type FilterOption } from '@/components/list/filters';
import { ListToolbar } from '@/components/list/ListToolbar';
import { NumberedPager } from '@/components/list/NumberedPager';
import { PageSizeSelect } from '@/components/list/PageSizeSelect';
import { type PageSize, useListParams } from '@/components/list/use-list-params';
import { useNarrow } from '@/components/list/use-narrow';
import { activeView, type ListView } from '@/components/list/ViewTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioOption } from '@/components/ui/radio-group';
import type { RowAction } from '@/components/ui/row-actions-menu';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { getActiveContext } from '@/lib/active-context-store';
import { cn } from '@/lib/utils';
import { countryOptions } from '@/profile/countries';
import { Badge } from '../../list/expense-labels';
import {
  EXPENSES_APPROVE,
  EXPENSES_MANAGE,
  useExpensesPermissions,
} from '../../use-expenses-permissions';
import { macroOf, SUPPLIER_TYPES, SupplierTypeList } from '../types';
import {
  type CatalogPage,
  exportSuppliers,
  listSuppliers,
  type Loaded,
  type SupplierRow,
  useLoaded,
} from './api';
import {
  applyView,
  catalogFilters,
  catalogQuery,
  CONTINENTS,
  FILTER_KEYS,
  isNarrowed,
  type ListState,
  MARK_FILTERS,
  monthLabel,
  OPEN_MARK_FILTERS,
  parseListState,
  type ResultsMode,
  type Sort,
  SORTS,
  systemViews,
  viewParams,
} from './list-state';
import { SuppliersMap } from './SuppliersMap';

/**
 * «Fornitori» (#958 piece H, report §3.8, boards 4.1–4.12): the organisation's suppliers,
 * worldwide. The list kit (U17): search, views, filters as chips, the order (most used first,
 * D246), «Elenco / Mappa», the CSV with its summary, pages of 50 with the total, and every choice
 * in the address. Rows show only the exception marks (D246). No «Nuovo fornitore» in phase 1:
 * suppliers are born in the expense form.
 */
export function SuppliersListPage() {
  const { t, i18n } = useTranslation('suppliers');
  const titleId = useId();
  const language = i18n.resolvedLanguage;
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseListState(params), [params]);
  const { active, set, clear, setPage, setPageSize } = useListParams(FILTER_KEYS, true);
  const gestione = useGestione();
  const narrow = useNarrow();

  const query = useMemo(() => catalogQuery(state), [state]);
  const listKey = JSON.stringify(query);
  const { state: list, reload } = useLoaded<CatalogPage>(listKey, () => listSuppliers(query));

  const personal = usePersonalViews();
  const views = useViews(gestione, personal.views);
  const current = useMemo(() => ({ get: (key: string) => params.get(key) }), [params]);
  const currentView = activeView(views, current);
  const currentPersonal = personal.views.find((view) => view.id === currentView?.id) ?? null;
  const onSelectView = useCallback(
    (view: ListView) => setParams((existing) => applyView(existing, view), { replace: true }),
    [setParams],
  );

  const rows = list.status === 'ready' ? list.items : [];
  const fields = useFilterFields(gestione, rows, active, language);
  const sortOptions = useSortOptions();
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const removePersonal = useCallback(
    (view: ListView) => {
      personal.remove(view.id);
      toast.success(t('list.views.removed', { name: view.label }));
    },
    [personal, t],
  );

  const more: RowAction[] = [
    {
      id: 'export',
      label: t('list.export.button'),
      icon: Download,
      onSelect: () => setExporting(true),
    },
    { id: 'save-view', label: t('list.views.save'), icon: Plus, onSelect: () => setSaving(true) },
    ...(currentPersonal
      ? [
          {
            id: 'remove-view',
            label: t('list.views.remove', { name: currentPersonal.label }),
            icon: Trash2,
            tone: 'destructive' as const,
            onSelect: () => removePersonal(currentPersonal),
          },
        ]
      : []),
  ];

  return (
    <section aria-labelledby={titleId} className="flex max-w-full min-w-0 flex-col gap-4">
      <PageHead
        titleId={titleId}
        title={t('list.title')}
        summary={t('list.summary')}
        actions={
          narrow ? undefined : (
            <Button variant="outline" onClick={() => setExporting(true)}>
              <Download />
              {t('list.export.button')}
            </Button>
          )
        }
      />
      <ListToolbar
        label={t('list.title')}
        search={{ label: t('list.search'), value: state.q, onWrite: (q) => set('q', q) }}
        fields={fields}
        active={active}
        onFilter={set}
        onClear={clear}
        sort={{
          label: t('list.sort.label'),
          value: state.sort,
          options: sortOptions,
          onChange: (value) => set('sort', value === 'uses' ? null : value),
        }}
        views={{ label: t('list.views.label'), items: views, current, onSelect: onSelectView }}
        viewsAside={
          <ViewActions
            current={currentPersonal}
            onSave={() => setSaving(true)}
            onRemove={removePersonal}
          />
        }
        more={more}
      />
      <ModeRow
        mode={state.mode}
        onMode={(mode) => set('view', mode === 'map' ? 'map' : null)}
        count={list.status === 'ready' ? list.total : null}
        sort={state.sort}
      />
      {state.mode === 'map' ? (
        <SuppliersMap
          filters={catalogFilters(state)}
          onShowList={() => set('view', null)}
          context={filterSummary(fields, active, state.q, t)}
        />
      ) : (
        <ListBody
          list={list}
          state={state}
          fields={fields}
          active={active}
          language={language}
          onFilter={set}
          onClear={clear}
          onReload={reload}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      )}
      <ExportPanel
        open={exporting}
        onOpenChange={setExporting}
        state={state}
        total={list.status === 'ready' ? list.total : null}
        pageRows={rows.length}
        scope={
          currentView && currentView.id !== 'all'
            ? t('list.export.view', { view: currentView.label })
            : filterSummary(fields, active, state.q, t)
        }
        sortLabel={sortOptions.find((option) => option.value === state.sort)?.label ?? ''}
      />
      <SaveViewPanel
        open={saving}
        onOpenChange={setSaving}
        taken={views.map((view) => view.label)}
        onSave={(name) => {
          const view = personal.save(name, viewParams(params));
          onSelectView(view);
          toast.success(t('list.views.saved', { name }));
        }}
      />
    </section>
  );
}

/** «Gestione» and the approvers: the status and mark queues (contract 0.2.1). */
function useGestione(): boolean {
  const permissions = useExpensesPermissions();
  return (
    permissions.status === 'ready' &&
    permissions.permissions.some((code) => code === EXPENSES_MANAGE || code === EXPENSES_APPROVE)
  );
}

function useViews(gestione: boolean, personal: readonly ListView[]): ListView[] {
  const { t } = useTranslation('suppliers');
  return useMemo(
    () => [
      ...systemViews(
        {
          all: t('list.views.all'),
          lodging: t('list.views.lodging'),
          food: t('list.views.food'),
          toComplete: t('list.views.toComplete'),
          toVerify: t('list.views.toVerify'),
          flagged: t('list.views.flagged'),
          intermediary: t('list.views.intermediary'),
          unusable: t('list.views.unusable'),
        },
        gestione,
      ),
      ...personal,
    ],
    [t, gestione, personal],
  );
}

/**
 * Personal views (U17, board 4.6). The console has no server storage for them yet (contract
 * 0.2.3: «a separate piece, for every list alike»), so they live in this browser, per person and
 * organisation.
 */
function usePersonalViews() {
  const uid = useAuth().user?.uid ?? 'anonymous';
  const organizationId = getActiveContext()?.organizationId ?? 'none';
  const key = `dgb.suppliers.views.${uid}.${organizationId}`;
  const [stored, setStored] = useState<{ key: string; views: ListView[] }>(() => ({
    key,
    views: readViews(key),
  }));
  const views = stored.key === key ? stored.views : readViews(key);
  const write = useCallback(
    (next: ListView[]) => {
      setStored({ key, views: next });
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // A full or blocked storage keeps the view for this visit only.
      }
    },
    [key],
  );
  return useMemo(
    () => ({
      views,
      save: (name: string, params: Record<string, string>): ListView => {
        const view: ListView = { id: `personal:${Date.now().toString(36)}`, label: name, params };
        write([...views, view]);
        return view;
      },
      remove: (id: string) => write(views.filter((view) => view.id !== id)),
    }),
    [views, write],
  );
}

function readViews(key: string): ListView[] {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (view): view is ListView =>
        typeof view === 'object' &&
        view !== null &&
        typeof (view as ListView).id === 'string' &&
        typeof (view as ListView).label === 'string' &&
        typeof (view as ListView).params === 'object',
    );
  } catch {
    return [];
  }
}

function ViewActions({
  current,
  onSave,
  onRemove,
}: {
  readonly current: ListView | null;
  readonly onSave: () => void;
  readonly onRemove: (view: ListView) => void;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <span className="flex shrink-0 items-center gap-1">
      {current ? (
        <Button
          variant="ghost"
          size="sm"
          className="min-h-11"
          aria-label={t('list.views.remove', { name: current.label })}
          onClick={() => onRemove(current)}
        >
          <Trash2 />
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" className="min-h-11" onClick={onSave}>
        <Plus />
        {t('list.views.save')}
      </Button>
    </span>
  );
}

function useSortOptions(): FilterOption[] {
  const { t } = useTranslation('suppliers');
  return useMemo(() => SORTS.map((value) => ({ value, label: t(`list.sort.${value}`) })), [t]);
}

/** A value of several (from a view or a link) gets its own option, so its chip reads well. */
function withActive(
  options: FilterOption[],
  value: string | null | undefined,
  label: (one: string) => string,
): FilterOption[] {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: value.split(',').map(label).join(', ') }];
}

/** Distinct values of a row field, for region and city: what the list shows, plus the one in force. */
function placeOptions(
  rows: readonly SupplierRow[],
  pick: (row: SupplierRow) => string | null,
  value: string | null | undefined,
  language: string | undefined,
): FilterOption[] {
  const seen = new Set(rows.map(pick).filter((v): v is string => v !== null && v !== ''));
  if (value) seen.add(value);
  const collator = new Intl.Collator(language ?? 'it');
  return [...seen].sort((a, b) => collator.compare(a, b)).map((v) => ({ value: v, label: v }));
}

function useFilterFields(
  gestione: boolean,
  rows: readonly SupplierRow[],
  active: Readonly<Record<string, string | null>>,
  language: string | undefined,
): FilterField[] {
  const { t } = useTranslation('suppliers');
  const countries = useMemo(() => countryOptions(language ?? 'it'), [language]);
  return useMemo(() => {
    const typeName = (value: string) => t(`type.${value}` as 'type.other');
    const continent = (value: string) => t(`list.continent.${value}` as 'list.continent.europe');
    const mark = (value: string) => t(`list.markFilter.${value}` as 'list.markFilter.flagged');
    const country = (code: string) => countries.find((c) => c.code === code)?.name ?? code;
    const marks = gestione ? MARK_FILTERS : OPEN_MARK_FILTERS;
    return [
      {
        // Several at once (#983 look check): each macro-category, its details indented under it.
        key: 'type',
        label: t('list.filters.type'),
        multiple: true,
        options: SUPPLIER_TYPES.map((value) => ({
          value,
          label: typeName(value),
          indent: macroOf(value) !== null,
        })),
      },
      {
        key: 'continent',
        label: t('list.filters.continent'),
        options: withActive(
          CONTINENTS.map((value) => ({ value, label: continent(value) })),
          active['continent'],
          continent,
        ),
      },
      {
        key: 'country',
        label: t('list.filters.country'),
        options: withActive(
          countries.map((c) => ({ value: c.code, label: c.name })),
          active['country'],
          country,
        ),
      },
      {
        key: 'region',
        label: t('list.filters.region'),
        options: placeOptions(rows, (row) => row.region, active['region'], language),
      },
      {
        key: 'city',
        label: t('list.filters.city'),
        options: placeOptions(rows, (row) => row.city, active['city'], language),
      },
      {
        key: 'mark',
        label: t('list.filters.mark'),
        options: withActive(
          marks.map((value) => ({ value, label: mark(value) })),
          active['mark'],
          mark,
        ),
      },
    ].filter((field) => field.options.length > 0 || active[field.key]);
  }, [t, gestione, rows, active, language, countries]);
}

/** «Tipo: Hotel, Continente: Europa» — what the export and the map say they hold. */
function filterSummary(
  fields: readonly FilterField[],
  active: Readonly<Record<string, string | null>>,
  q: string,
  t: ReturnType<typeof useTranslation<'suppliers'>>['t'],
): string {
  const parts = fields
    .filter((field) => active[field.key])
    .map((field) => {
      return `${field.label} ${optionLabel(field, active[field.key] ?? '')}`;
    });
  if (q !== '') parts.unshift(t('list.export.search', { q }));
  return parts.length === 0
    ? t('list.export.noFilters')
    : t('list.export.filters', { filters: parts.join(', ') });
}

/** «Elenco / Mappa» (U24) and how many suppliers the filters keep. */
function ModeRow({
  mode,
  onMode,
  count,
  sort,
}: {
  readonly mode: ResultsMode;
  readonly onMode: (mode: ResultsMode) => void;
  readonly count: number | null;
  readonly sort: Sort;
}) {
  const { t } = useTranslation('suppliers');
  const choices = [
    { id: 'list' as const, label: t('list.mode.list'), icon: List },
    { id: 'map' as const, label: t('list.mode.map'), icon: MapIcon },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label={t('list.mode.label')} className="flex shrink-0 gap-1">
        {choices.map((choice) => {
          const pressed = choice.id === mode;
          return (
            <Button
              key={choice.id}
              type="button"
              size="sm"
              variant="outline"
              aria-pressed={pressed}
              className={cn(
                'min-h-11 text-sm',
                pressed &&
                  'border-selected-border bg-selected text-selected-foreground hover:bg-selected font-semibold',
              )}
              onClick={() => onMode(choice.id)}
            >
              <choice.icon aria-hidden="true" />
              {choice.label}
            </Button>
          );
        })}
      </div>
      {count !== null && mode === 'list' ? (
        <span className="text-muted-foreground text-sm" aria-live="polite">
          {t('list.count', { count })} · {t(`list.sort.${sort}`)}
        </span>
      ) : null}
    </div>
  );
}

function ListBody({
  list,
  state,
  fields,
  active,
  language,
  onFilter,
  onClear,
  onReload,
  onPage,
  onPageSize,
}: {
  readonly list: Loaded<CatalogPage>;
  readonly state: ListState;
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly language: string | undefined;
  readonly onFilter: (key: string, value: string | null) => void;
  readonly onClear: () => void;
  readonly onReload: () => void;
  readonly onPage: (page: number) => void;
  readonly onPageSize: (size: PageSize) => void;
}) {
  const { t } = useTranslation('suppliers');
  if (list.status === 'loading') {
    return (
      <SkeletonRegion label={t('list.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3, 4].map((n) => (
          <Skeleton key={n} className="h-12 w-full" />
        ))}
      </SkeletonRegion>
    );
  }
  if (list.status === 'error') {
    const forbidden = list.problem.code === 'LIST_FILTER_FORBIDDEN';
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={Store}
        title={forbidden ? (list.problem.message ?? t('list.forbidden')) : t('list.loadError')}
        action={
          forbidden ? (
            <Button variant="outline" onClick={onClear}>
              {t('list.noResults.clear')}
            </Button>
          ) : (
            <Button variant="outline" onClick={onReload}>
              {t('list.retry')}
            </Button>
          )
        }
      />
    );
  }
  if (list.items.length === 0) {
    return isNarrowed(state) ? (
      <NoResults fields={fields} active={active} onFilter={onFilter} onClear={onClear} />
    ) : (
      <StateMessage
        icon={Store}
        title={t('list.empty.title')}
        text={t('list.empty.text')}
        action={
          <Button asChild>
            <Link to="/expenses/new">
              <Plus />
              {t('list.empty.action')}
            </Link>
          </Button>
        }
      />
    );
  }
  const from = (state.page - 1) * state.pageSize + 1;
  return (
    <>
      <SuppliersTable items={list.items} language={language} />
      <SupplierCards items={list.items} language={language} />
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-3 text-sm">
        <span>
          {t('list.range', { from, to: from + list.items.length - 1, total: list.total })}
        </span>
        <PageSizeSelect value={state.pageSize as PageSize} onChange={onPageSize} />
      </div>
      <NumberedPager
        page={state.page}
        pageSize={state.pageSize}
        total={list.total}
        onPage={onPage}
      />
    </>
  );
}

/** Board 4.12: different from the empty catalog, it offers to change the filters, not to create. */
function NoResults({
  fields,
  active,
  onFilter,
  onClear,
}: {
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly onFilter: (key: string, value: string | null) => void;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('suppliers');
  const last = [...fields].reverse().find((field) => active[field.key]);
  const value = last ? (active[last.key] ?? '') : '';
  return (
    <StateMessage
      icon={SearchX}
      title={t('list.noResults.title')}
      text={t('list.noResults.text')}
      action={
        <span className="flex flex-wrap justify-center gap-2">
          {last ? (
            <Button variant="outline" onClick={() => onFilter(last.key, null)}>
              {t('list.noResults.remove', {
                label: last.label,
                value: last.options.find((o) => o.value === value)?.label ?? value,
              })}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClear}>
            {t('list.noResults.clear')}
          </Button>
        </span>
      }
    />
  );
}

const supplierPath = (id: string) => `/expenses/suppliers/${encodeURIComponent(id)}`;

/** D246: only what is out of the ordinary; an active, complete supplier shows nothing. */
function ExceptionMarks({ row }: { readonly row: SupplierRow }) {
  const { t } = useTranslation('suppliers');
  const marks = [
    row.status !== 'active' ? (
      <Badge key="status" tone="neutral">
        {t(`status.${row.status}`)}
      </Badge>
    ) : null,
    row.completeness === 'to_complete' ? (
      <Badge key="complete" tone="warning">
        {t('marks.toComplete')}
      </Badge>
    ) : null,
    row.toVerify ? (
      <Badge key="verify" tone="warning">
        {t('marks.toVerify')}
      </Badge>
    ) : null,
    row.intermediary ? (
      <Badge key="intermediary" tone="info">
        {t('marks.intermediary')}
      </Badge>
    ) : null,
    row.flagged ? (
      <Badge key="flagged" tone="error">
        {t('marks.flagged')}
      </Badge>
    ) : null,
  ].filter(Boolean);
  if (marks.length === 0) return null;
  return <span className="flex flex-wrap gap-1">{marks}</span>;
}

function Categories({ row }: { readonly row: SupplierRow }) {
  return <SupplierTypeList types={row.types} />;
}

function countryName(code: string | null, language: string | undefined): string | null {
  if (code === null) return null;
  try {
    return new Intl.DisplayNames([language ?? 'it'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Board 4.1 at 1512, 4.4 at 1024: country, sites and last use leave below 1200 px of shell. */
const WIDE = 'hidden @min-[1200px]/shell:table-cell';

function SuppliersTable({
  items,
  language,
}: {
  readonly items: readonly SupplierRow[];
  readonly language: string | undefined;
}) {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const th = 'px-3 py-2.5 font-semibold';
  return (
    <div className="border-border hidden overflow-x-auto rounded-xl border @min-[620px]/shell:block">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t('list.title')}</caption>
        <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
          <tr>
            <th scope="col" className={th}>
              {t('list.columns.supplier')}
            </th>
            <th scope="col" className={th}>
              {t('list.columns.type')}
            </th>
            <th scope="col" className={th}>
              {t('list.columns.city')}
            </th>
            <th scope="col" className={cn(th, WIDE)}>
              {t('list.columns.country')}
            </th>
            <th scope="col" className={cn(th, WIDE, 'text-right')}>
              {t('list.columns.sites')}
            </th>
            <th scope="col" className={cn(th, 'text-right')}>
              {t('list.columns.uses')}
            </th>
            <th scope="col" className={cn(th, WIDE)}>
              {t('list.columns.lastUsed')}
            </th>
            <th scope="col" className={th}>
              {t('list.columns.marks')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <ClickableRow
              key={row.id}
              className="border-border hover:bg-muted/40 border-t"
              onOpen={() => void navigate(supplierPath(row.id))}
            >
              <td className="px-3 py-3">
                <Link
                  to={supplierPath(row.id)}
                  className="text-foreground font-semibold hover:underline"
                >
                  {row.name}
                </Link>
                {row.legalName && row.legalName !== row.name ? (
                  <small className="text-muted-foreground block">{row.legalName}</small>
                ) : null}
              </td>
              <td className="px-3 py-3">
                <Categories row={row} />
              </td>
              <td className="px-3 py-3">
                {row.city ?? '—'}
                {row.region ? (
                  <small className="text-muted-foreground block">{row.region}</small>
                ) : null}
              </td>
              <td className={cn('px-3 py-3', WIDE)}>{countryName(row.country, language) ?? '—'}</td>
              <td className={cn('px-3 py-3 text-right tabular-nums', WIDE)}>{row.siteCount}</td>
              <td className="px-3 py-3 text-right tabular-nums">{row.uses}</td>
              <td className={cn('px-3 py-3 whitespace-nowrap', WIDE)}>
                {monthLabel(row.lastUsed, language) ?? '—'}
              </td>
              <td className="px-3 py-3">
                <ExceptionMarks row={row} />
              </td>
            </ClickableRow>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Board 4.9: the name and the uses, the type and the place, the marks only when there are some. */
function SupplierCards({
  items,
  language,
}: {
  readonly items: readonly SupplierRow[];
  readonly language: string | undefined;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <ul aria-label={t('list.title')} className="flex flex-col gap-2 @min-[620px]/shell:hidden">
      {items.map((row) => {
        const place = [row.city, countryName(row.country, language)].filter(Boolean).join(', ');
        return (
          <li key={row.id}>
            <Link
              to={supplierPath(row.id)}
              className="border-border bg-card flex flex-col gap-1 rounded-xl border p-3"
            >
              <span className="flex items-baseline gap-2">
                <b className="min-w-0 flex-1 truncate">{row.name}</b>
                <span className="text-muted-foreground text-sm whitespace-nowrap tabular-nums">
                  {t('list.uses', { count: row.uses })}
                </span>
              </span>
              {/* Two lines, no «·» between them: the types wrap on a phone (#983 look check). */}
              <small className="text-muted-foreground flex flex-col gap-0.5">
                <Categories row={row} />
                {place ? <span>{place}</span> : null}
              </small>
              <ExceptionMarks row={row} />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Board 4.7: the summary says what leaves — the set, the order and the columns (U17). */
function ExportPanel({
  open,
  onOpenChange,
  state,
  total,
  pageRows,
  scope,
  sortLabel,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly state: ListState;
  readonly total: number | null;
  readonly pageRows: number;
  readonly scope: string;
  readonly sortLabel: string;
}) {
  const { t } = useTranslation('suppliers');
  const name = useId();
  const [onlyPage, setOnlyPage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'limit' | 'other' | null>(null);
  const count = onlyPage ? pageRows : (total ?? 0);
  async function run() {
    setBusy(true);
    setFailed(null);
    try {
      await exportSuppliers(catalogQuery(state), onlyPage, t('list.export.fileName'));
      onOpenChange(false);
    } catch (error) {
      const code = typeof error === 'object' && error !== null && 'code' in error ? error.code : '';
      setFailed(code === 'EXPORT_ROW_LIMIT_EXCEEDED' ? 'limit' : 'other');
    } finally {
      setBusy(false);
    }
  }
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) setFailed(null);
        onOpenChange(next);
      }}
      title={t('list.export.title')}
      description={`${scope}; ${t('list.export.order', { sort: sortLabel })}.`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('list.export.cancel')}
          </Button>
          <Button disabled={busy || count === 0} onClick={() => void run()}>
            <Download />
            {t('list.export.submit', { count })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup legend={t('list.export.choice')}>
          <RadioOption
            name={name}
            label={t('list.export.all', { count: total ?? 0 })}
            help={t('list.export.allHelp')}
            checked={!onlyPage}
            onSelect={() => setOnlyPage(false)}
          />
          <RadioOption
            name={name}
            label={t('list.export.page')}
            help={t('list.export.pageHelp', { count: pageRows })}
            checked={onlyPage}
            onSelect={() => setOnlyPage(true)}
          />
        </RadioGroup>
        <div className="bg-muted/50 flex flex-col gap-1.5 rounded-lg p-3 text-sm">
          <b>{t('list.export.columnsTitle')}</b>
          {/* The file's own header, in Italian in every language (contract: exportSuppliers):
              the summary names the columns as the file will, so they are not translated. */}
          <span className="text-muted-foreground">{t('list.export.columns')}</span>
          <small className="text-muted-foreground">{t('list.export.columnsHelp')}</small>
        </div>
        {failed ? (
          <p role="alert" className="text-destructive text-sm">
            {t(failed === 'limit' ? 'list.export.tooLarge' : 'list.export.failed')}
          </p>
        ) : null}
      </div>
    </SidePanel>
  );
}

function SaveViewPanel({
  open,
  onOpenChange,
  taken,
  onSave,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly taken: readonly string[];
  readonly onSave: (name: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const inputId = useId();
  const errorId = useId();
  const formId = useId();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') return setError(t('list.views.nameRequired'));
    if (taken.some((label) => label.toLowerCase() === trimmed.toLowerCase())) {
      return setError(t('list.views.nameTaken'));
    }
    onSave(trimmed);
    setName('');
    setError(null);
    onOpenChange(false);
  }
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) setError(null);
        onOpenChange(next);
      }}
      title={t('list.views.saveTitle')}
      description={t('list.views.saveHelp')}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('list.export.cancel')}
          </Button>
          <Button type="submit" form={formId}>
            {t('list.views.save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-1.5">
        <label htmlFor={inputId} className="text-sm font-semibold">
          {t('list.views.name')}
        </label>
        <Input
          id={inputId}
          value={name}
          maxLength={60}
          aria-invalid={error !== null}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {error ? (
          <p id={errorId} role="alert" className="text-destructive text-sm">
            {error}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
